import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  Gift, Plus, UploadSimple, Wallet, CurrencyInr, ArrowClockwise, Percent, Eye, EyeSlash,
  Buildings, CreditCard, ArrowSquareOut, Trash, Key, MagnifyingGlass,
  CheckCircle, XCircle, Clock, Receipt, CalendarBlank,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import AdminCardUploadDialog from '../components/vendor-cards/AdminCardUploadDialog';
import ElectricityBulkSection from '../components/vendors/ElectricityBulkSection';
import VendorLedgerSection from '../components/vendor/VendorLedgerSection';
import PasswordResetDialog from '../components/admin/PasswordResetDialog';
import DuplicateScannerModal from '../components/vendor-cards/DuplicateScannerModal';

function fmt(n) {
  if (typeof n !== 'number') return n;
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
}

// ─── Reusable Date Picker Button (Popover + Calendar) ─────────────
function DatePickerButton({ value, onChange, disabled, testid, allowFuture = false }) {
  const [open, setOpen] = useState(false);
  const parsed = value ? new Date(`${value}T12:00:00`) : new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const label = value
    ? new Date(`${value}T12:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'Select date';
  const isToday = value === new Date().toISOString().slice(0, 10);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid={testid}
          className="w-full h-10 flex items-center justify-between gap-2 px-3 border rounded-md bg-white text-sm hover:border-indigo-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition disabled:opacity-50"
        >
          <span className="flex items-center gap-2">
            <CalendarBlank size={14} className="text-indigo-600" />
            <span className="font-medium">{label}</span>
            {isToday && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold">TODAY</span>}
          </span>
          <span className="text-[10px] text-gray-400">▼</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-2 border-b bg-gray-50 flex flex-wrap gap-1 text-xs">
          {[
            { label: 'Today', offset: 0 },
            { label: 'Yesterday', offset: -1 },
            { label: '2 days ago', offset: -2 },
            { label: '1 week ago', offset: -7 },
          ].map((q) => (
            <button
              key={q.offset}
              type="button"
              className="px-2 py-1 border rounded bg-white hover:bg-indigo-50 hover:border-indigo-300"
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() + q.offset);
                onChange(d.toISOString().slice(0, 10));
                setOpen(false);
              }}
            >{q.label}</button>
          ))}
        </div>
        <Calendar
          mode="single"
          selected={parsed}
          onSelect={(d) => {
            if (!d) return;
            const s = d.toISOString().slice(0, 10);
            onChange(s);
            setOpen(false);
          }}
          disabled={allowFuture ? undefined : (d) => d > today}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/** Category tabs with per-category summary + filtered table.
 * Rule #4: Each vendor category shown separately with its own totals.
 */
function VendorCategoryTabbedTable({ vendors, activeCategory, setActiveCategory, isReadOnly, showDelete, onOpen, onUpload, onDelete, onResetPw }) {
  // Canonical vendor categories
  const CATS = [
    { key: 'all',         label: 'All Vendors',         active: 'border-fuchsia-600 text-fuchsia-700 bg-fuchsia-50' },
    { key: 'gift_card',   label: 'Gift Card Vendors',   active: 'border-purple-600 text-purple-700 bg-purple-50' },
    { key: 'card',        label: 'Card Vendors',        active: 'border-indigo-600 text-indigo-700 bg-indigo-50' },
    { key: 'recharge',    label: 'Recharge Vendors',    active: 'border-sky-600 text-sky-700 bg-sky-50' },
    { key: 'electricity', label: 'Electricity Vendors', active: 'border-amber-600 text-amber-700 bg-amber-50' },
  ];

  // Per-category stats
  const statsFor = (cat) => {
    const list = cat === 'all' ? vendors : vendors.filter((v) => v.vendor_type === cat);
    return list.reduce((acc, v) => ({
      count: acc.count + 1,
      credit: acc.credit + (v.credit || 0),
      debit: acc.debit + (v.debit || 0),
      balance: acc.balance + (v.balance || 0),
      cards: acc.cards + (v.total_cards_uploaded || 0),
    }), { count: 0, credit: 0, debit: 0, balance: 0, cards: 0 });
  };

  const filtered = activeCategory === 'all'
    ? vendors
    : vendors.filter((v) => v.vendor_type === activeCategory);

  const activeCat = CATS.find((c) => c.key === activeCategory) || CATS[0];
  const activeStats = statsFor(activeCategory);

  return (
    <div data-testid="vendor-category-tabbed-table">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 px-3 pt-3 pb-0 overflow-x-auto">
        {CATS.map((c) => {
          const s = statsFor(c.key);
          const isActive = activeCategory === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveCategory(c.key)}
              data-testid={`vendor-cat-tab-${c.key}`}
              className={`px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-t-md border-b-2 transition ${
                isActive
                  ? c.active
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {c.label} <span className="ml-1 text-[10px] font-normal opacity-70">({s.count})</span>
            </button>
          );
        })}
      </div>

      {/* Category summary cards — clear separate calculation per Rule #4 */}
      <div className="px-4 pt-4 pb-2 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid={`vendor-cat-stats-${activeCategory}`}>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Category</div>
          <div className="text-sm font-bold text-gray-900 mt-0.5">{activeCat.label}</div>
          <div className="text-[10px] text-gray-500">{activeStats.count} vendors</div>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Total Cards</div>
          <div className="text-lg font-bold text-indigo-900 mt-0.5">{activeStats.cards}</div>
          <div className="text-[10px] text-indigo-600">uploaded / issued</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Total Credited</div>
          <div className="text-lg font-bold text-emerald-900 mt-0.5">{fmt(activeStats.credit)}</div>
          <div className="text-[10px] text-emerald-600">Cr sum</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-red-700 font-semibold">Total Paid Out</div>
          <div className="text-lg font-bold text-red-900 mt-0.5">{fmt(activeStats.debit)}</div>
          <div className="text-[10px] text-red-600">Dr sum</div>
        </div>
        <div className={`${activeStats.balance >= 0 ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900' : 'bg-amber-50 border-amber-200 text-amber-900'} border rounded-lg p-3`}>
          <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">Net Balance (owed)</div>
          <div className="text-lg font-bold mt-0.5">{fmt(activeStats.balance)}</div>
          <div className="text-[10px] opacity-70">Cr − Dr</div>
        </div>
      </div>

      {/* Vendor table for selected category */}
      {filtered.length === 0 ? (
        <div className="p-12 text-center text-sm text-gray-500">
          <Gift size={40} weight="duotone" className="mx-auto text-gray-300 mb-2" />
          Is category me abhi koi vendor nahi hai.
        </div>
      ) : (
        <div className="overflow-x-auto px-2 pb-2">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-right">Discount %</th>
                <th className="px-3 py-2 text-right">Total Cards</th>
                <th className="px-3 py-2 text-right">Credited</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right">Balance (owed)</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => (
                <tr key={v.id} className="border-t hover:bg-fuchsia-50/40" data-testid={`gc-vendor-row-${v.id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">{v.name}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{v.email}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                      v.vendor_type === 'card' ? 'bg-indigo-100 text-indigo-700' :
                      v.vendor_type === 'gift_card' ? 'bg-purple-100 text-purple-700' :
                      v.vendor_type === 'recharge' ? 'bg-sky-100 text-sky-700' :
                      v.vendor_type === 'electricity' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {(v.vendor_type || '').replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{v.discount_percent}%</td>
                  <td className="px-3 py-2 text-right">{v.total_cards_uploaded}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{fmt(v.credit)}</td>
                  <td className="px-3 py-2 text-right text-red-700">{fmt(v.debit)}</td>
                  <td className={`px-3 py-2 text-right font-bold ${v.balance >= 0 ? 'text-fuchsia-700' : 'text-amber-700'}`}>{fmt(v.balance)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center gap-1 justify-end flex-nowrap whitespace-nowrap">
                      {v.vendor_type === 'card' && !isReadOnly && (
                        <Button
                          size="sm"
                          onClick={() => onUpload(v)}
                          className="bg-indigo-600 hover:bg-indigo-700 gap-1 h-8 text-xs"
                          data-testid={`row-upload-cards-${v.id}`}
                          title="Upload cards directly to this vendor (auto-debits wallet)"
                        >
                          <UploadSimple size={12} weight="bold" /> + Cards
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => onOpen(v.id)} data-testid={`open-vendor-${v.id}`}>
                        Open
                      </Button>
                      {!isReadOnly && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onResetPw(v)}
                          className="h-8 px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                          title={`Reset password for ${v.name}`}
                          data-testid={`reset-pw-vendor-${v.id}`}
                        >
                          <Key size={14} weight="bold" />
                        </Button>
                      )}
                      {!isReadOnly && showDelete && (
                        <Button
                          size="sm"
                          onClick={() => onDelete(v)}
                          className="h-8 px-3 gap-1 bg-red-600 hover:bg-red-700 text-white font-bold border border-red-700 shadow-sm"
                          title={`Delete vendor "${v.name}" — same email se naye category me phir signup kar sakte ho`}
                          data-testid={`delete-vendor-${v.id}`}
                        >
                          <Trash size={14} weight="bold" /> Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Component used by Admin/CA — list of vendors with create / open details */
function AdminView({ isReadOnly, viewerRole }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', vendor_type: '', discount_percent: 1.5, initial_balance: 0 });
  const [showPass, setShowPass] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploadVendor, setUploadVendor] = useState(null);  // { id, name, discount_percent }
  const [resetPwVendor, setResetPwVendor] = useState(null);  // { id, name, email, vendor_type }
  const [showDupeScanner, setShowDupeScanner] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');  // all | gift_card | card | recharge | electricity

  // Show/Hide delete buttons across the vendor list — owner-controlled toggle
  // persisted in localStorage. Default = hidden (safer; user can enable on
  // demand and disable back when done). Per user spec Jun 2026:
  //   "hide ho sakta hai kya ye delete wala.....bad me chahu to ek baar me
  //    enable v ho jay"
  const [showDelete, setShowDelete] = useState(() => {
    try { return localStorage.getItem('vendor_show_delete') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('vendor_show_delete', showDelete ? '1' : '0'); } catch (_e) { /* ignore */ }
  }, [showDelete]);

  // Vendor-type config — used by the Create dialog category selector.
  // Order matches the tab order on the page.
  const VENDOR_TYPE_OPTIONS = [
    { value: 'gift_card',   label: 'Gift Card Vendor',  default_discount: 1.5,  icon: '🎁' },
    { value: 'card',        label: 'Card Vendor',       default_discount: 1.5,  icon: '💳' },
    { value: 'recharge',    label: 'Recharge Vendor',   default_discount: 0,    icon: '📱' },
    { value: 'electricity', label: 'Electricity Vendor', default_discount: 0,   icon: '⚡' },
  ];

  // Whenever the create dialog opens, vendor_type is ALWAYS reset to empty —
  // user MUST explicitly click a category card every time. No auto-defaulting
  // from the active tab (per user spec Jun 2026: "auto set nahi hona chahiye…
  // category puchhna jaruri karo… fill kar ke hi create ho sahi").
  const openCreateDialog = () => {
    setForm({
      name: '', email: '', password: '', phone: '',
      vendor_type: '',
      discount_percent: '',
      initial_balance: 0,
    });
    setShowCreate(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendors/');
      setVendors(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createVendor = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email, password zaroori hain'); return;
    }
    if (!form.vendor_type || !VENDOR_TYPE_OPTIONS.find(o => o.value === form.vendor_type)) {
      toast.error('Vendor Category pehle select karo — Gift Card / Card / Recharge / Electricity me se ek choose karo');
      return;
    }
    setCreating(true);
    try {
      await api.post('/vendors/create', {
        ...form,
        vendor_type: form.vendor_type,
        discount_percent: Number(form.discount_percent),
        initial_balance: Number(form.initial_balance) || 0,
      });
      const label = VENDOR_TYPE_OPTIONS.find(o => o.value === form.vendor_type)?.label || 'Vendor';
      toast.success(`${label} created`);
      setShowCreate(false);
      await load();
      // Switch tab to the category the user just created so the new vendor is
      // immediately visible — no more "where did it go?" confusion.
      setActiveCategory(form.vendor_type);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  if (activeId) {
    return (
      <VendorDetail
        vendorId={activeId}
        onBack={() => { setActiveId(null); load(); }}
        isReadOnly={isReadOnly}
        viewerRole={viewerRole}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="gc-vendors-admin-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gift size={26} weight="duotone" className="text-fuchsia-600" />
            Gift Card Vendors
          </h1>
          <p className="text-sm text-gray-500">Vendor accounts, discounts, fund payments aur ledger</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="gap-1"><ArrowClockwise size={14} /> Refresh</Button>
          {!isReadOnly && (
            <Button
              variant="outline"
              onClick={() => setShowDupeScanner(true)}
              className="gap-1 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
              data-testid="scan-duplicates-btn"
              title="Scan all card vendors for duplicate card numbers"
            >
              <MagnifyingGlass size={14} weight="bold" /> Scan Duplicates
            </Button>
          )}
          {!isReadOnly && (
            <Button
              variant="outline"
              onClick={() => setShowDelete(v => !v)}
              className={`gap-1 ${showDelete ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100' : 'border-gray-300'}`}
              data-testid="toggle-delete-buttons"
              title={showDelete ? 'Delete buttons hide karne ke liye click karo' : 'Delete buttons dikhane ke liye click karo'}
            >
              <Trash size={14} weight={showDelete ? 'fill' : 'regular'} />
              {showDelete ? 'Hide Delete' : 'Show Delete'}
            </Button>
          )}
          {!isReadOnly && (
            <Dialog open={showCreate} onOpenChange={(open) => open ? openCreateDialog() : setShowCreate(false)}>
              <DialogTrigger asChild>
                <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 gap-1" data-testid="create-gc-vendor-btn">
                  <Plus size={14} weight="bold" /> New Vendor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {form.vendor_type
                      ? `Create ${VENDOR_TYPE_OPTIONS.find(o => o.value === form.vendor_type)?.label}`
                      : 'Create New Vendor — Category Choose Karo'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  {/* Vendor Category — explicit & required. No default when
                      opened from "All Vendors" tab; user MUST click one. */}
                  <div className={!form.vendor_type ? 'p-2 rounded border-2 border-red-400 bg-red-50' : ''}>
                    <Label className={`font-bold ${!form.vendor_type ? 'text-red-700' : 'text-fuchsia-900'}`}>
                      Vendor Category *
                      {!form.vendor_type && <span className="ml-2 text-[11px] font-normal">⚠️ Pehle category select karo</span>}
                    </Label>
                    <div className="grid grid-cols-2 gap-2 mt-1" data-testid="gc-category-grid">
                      {VENDOR_TYPE_OPTIONS.map(opt => (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={() => setForm({ ...form, vendor_type: opt.value, discount_percent: opt.default_discount })}
                          className={`text-left px-3 py-2 rounded border-2 transition ${
                            form.vendor_type === opt.value
                              ? 'border-fuchsia-500 bg-fuchsia-50 ring-2 ring-fuchsia-200'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                          data-testid={`gc-category-${opt.value}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{opt.icon}</span>
                            <div>
                              <div className="text-sm font-semibold text-gray-900">{opt.label}</div>
                              <div className="text-[10px] text-gray-500">Default discount: {opt.default_discount}%</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="gc-name" />
                  </div>
                  <div>
                    <Label>Email (login)</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="gc-email" />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <div className="relative">
                      <Input type={showPass ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="gc-password" />
                      <button type="button" onClick={() => setShowPass(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                        {showPass ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Phone</Label>
                      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div>
                      <Label>Discount %</Label>
                      <Input type="number" step="0.01" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="gc-discount" />
                    </div>
                  </div>
                  <div>
                    <Label>Opening Balance (optional)</Label>
                    <Input type="number" step="0.01" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                  <Button
                    onClick={createVendor}
                    disabled={creating || !form.vendor_type}
                    className="bg-fuchsia-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="gc-create-submit"
                    title={!form.vendor_type ? 'Pehle Vendor Category choose karo' : ''}
                  >
                    {creating ? 'Creating...' : (form.vendor_type
                      ? `Create ${VENDOR_TYPE_OPTIONS.find(o => o.value === form.vendor_type)?.label}`
                      : 'Select Category first')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        ) : vendors.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            <Gift size={40} weight="duotone" className="mx-auto text-gray-300 mb-2" />
            Abhi koi vendor nahi hai. "New Vendor" se create karo.
          </div>
        ) : (
          <VendorCategoryTabbedTable
            vendors={vendors}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            isReadOnly={isReadOnly}
            showDelete={showDelete}
            onOpen={(id) => setActiveId(id)}
            onUpload={(v) => setUploadVendor({ id: v.id, name: v.name, discount_percent: v.discount_percent })}
            onResetPw={(v) => setResetPwVendor({ id: v.id, name: v.name, email: v.email, vendor_type: v.vendor_type })}
            onDelete={async (v) => {
              const baseConfirm = window.confirm(
                `Vendor "${v.name}" ko delete karna hai?\n\n` +
                `Type: ${(v.vendor_type || '').toUpperCase()}\n` +
                `Cards uploaded: ${v.total_cards_uploaded || 0}\n` +
                `Balance owed: ₹${(v.balance || 0).toFixed(2)}\n\n` +
                `Yeh sirf tab use karo jab galti se galat category me register hua ho. ` +
                `Delete ke baad same email se naye category me phir signup kar sakte ho.`
              );
              if (!baseConfirm) return;
              try {
                await api.delete(`/vendors/${v.id}`);
                toast.success(`Vendor "${v.name}" deleted. Email free ho gya hai.`);
                await load();
              } catch (err) {
                const msg = err?.response?.data?.detail || 'Delete failed';
                // If vendor has cards/ledger, offer force option
                if (err?.response?.status === 400 && (v.total_cards_uploaded > 0 || msg.toLowerCase().includes('cards') || msg.toLowerCase().includes('ledger'))) {
                  const force = window.confirm(
                    `${msg}\n\n` +
                    `FORCE DELETE karne par cards aur ledger entries bhi soft-delete ho jayengi (audit ke liye retain rahega).\n\n` +
                    `Pakka force delete karna hai?`
                  );
                  if (force) {
                    try {
                      await api.delete(`/vendors/${v.id}?force=true`);
                      toast.success(`Vendor "${v.name}" force-deleted with cascade.`);
                      await load();
                    } catch (e2) {
                      toast.error(e2?.response?.data?.detail || 'Force delete failed');
                    }
                  }
                } else {
                  toast.error(msg);
                }
              }
            }}
          />
        )}
      </div>

      {/* Direct upload dialog from list row (card vendors only) */}
      {uploadVendor && (
        <AdminCardUploadDialog
          open={!!uploadVendor}
          onOpenChange={(v) => { if (!v) setUploadVendor(null); }}
          vendorId={uploadVendor.id}
          vendorName={uploadVendor.name}
          defaultDiscount={uploadVendor.discount_percent}
          onDone={async () => { setUploadVendor(null); await load(); }}
        />
      )}

      {/* Admin password reset for any vendor row */}
      {resetPwVendor && (
        <PasswordResetDialog
          open={!!resetPwVendor}
          onOpenChange={(v) => { if (!v) setResetPwVendor(null); }}
          userId={resetPwVendor.id}
          userName={resetPwVendor.name}
          userEmail={resetPwVendor.email}
          userRole={`${resetPwVendor.vendor_type}_vendor`}
        />
      )}

      {/* Duplicate Scanner — scans all card_vendors for repeated card numbers */}
      <DuplicateScannerModal
        open={showDupeScanner}
        onOpenChange={setShowDupeScanner}
      />
    </div>
  );
}

/** Component used by both Admin & Vendor themselves — shows ledger + upload + fund-add */
function VendorDetail({ vendorId, onBack, isReadOnly, isSelfVendor, viewerRole }) {
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState({ entries: [], balance: null });
  const [loading, setLoading] = useState(true);
  const [showFund, setShowFund] = useState(false);
  const _today = new Date().toISOString().slice(0, 10);
  const [fundForm, setFundForm] = useState({
    amount: '', payment_mode: 'upi', receipt_number: '', upi_txn_id: '',
    account_holder: '', account_number: '', ifsc: '', txn_reference: '', remarks: '',
    payment_date: _today,
  });
  const [fundFile, setFundFile] = useState(null);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [showDiscount, setShowDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState(0);
  // Structured entry form + pending queue
  const [entryForm, setEntryForm] = useState({ card_count: '', per_card_amount: '', discount_override: '', remarks: '', entry_date: new Date().toISOString().slice(0, 10) });
  const [entryFile, setEntryFile] = useState(null);
  const [myEntries, setMyEntries] = useState([]);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [cardSummary, setCardSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, led, ents] = await Promise.all([
        api.get(`/vendors/${vendorId}`),
        api.get(`/vendors/${vendorId}/ledger`),
        api.get(`/vendors/${vendorId}/entries`),
      ]);
      setSummary(sum.data);
      setLedger(led.data);
      setMyEntries(ents.data || []);
      setNewDiscount(sum.data.discount_percent);
      // Card vendors: load cards summary for Used/Unused tracking
      if (sum.data?.vendor_type === 'card') {
        try {
          const cs = await api.get(`/vendor-cards/summary?vendor_id=${vendorId}`);
          setCardSummary(cs.data);
        } catch { /* 403 on non-admin/non-self — fine */ }
      }
    } catch (err) {
      toast.error('Load failed');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  const loadPending = useCallback(async () => {
    try {
      const r = await api.get('/vendors/entries/pending');
      setPendingEntries(r.data || []);
    } catch {/* ignore */}
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const r = await api.get(`/vendors/${vendorId}/saved-accounts`);
      setSavedAccounts(r.data || []);
    } catch {/* ignore */}
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!isSelfVendor && !isReadOnly) loadPending(); }, [isSelfVendor, isReadOnly, loadPending]);
  useEffect(() => { if (!isSelfVendor && !isReadOnly) loadAccounts(); }, [isSelfVendor, isReadOnly, loadAccounts]);

  // ─── Structured Entry Submission (vendor or admin-on-behalf) ───
  const submitEntry = async () => {
    const cc = Number(entryForm.card_count);
    const pa = Number(entryForm.per_card_amount);
    if (!cc || !pa || cc <= 0 || pa <= 0) {
      toast.error('Cards count & per-card amount dalo'); return;
    }
    const fd = new FormData();
    fd.append('card_count', String(cc));
    fd.append('per_card_amount', String(pa));
    if (entryForm.discount_override) fd.append('discount_percent', String(Number(entryForm.discount_override)));
    if (entryForm.remarks) fd.append('remarks', entryForm.remarks);
    if (entryForm.entry_date) fd.append('entry_date', entryForm.entry_date);
    if (entryFile) fd.append('receipt', entryFile);
    try {
      const r = await api.post(`/vendors/${vendorId}/submit-entry`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(r.data.message);
      setEntryForm({ card_count: '', per_card_amount: '', discount_override: '', remarks: '', entry_date: new Date().toISOString().slice(0, 10) });
      setEntryFile(null);
      await load();
      if (!isSelfVendor) await loadPending();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    }
  };

  const approveEntry = async (eid) => {
    if (!window.confirm('Entry approve karni hai? Ledger me credit auto-add ho jayega.')) return;
    try {
      const r = await api.post(`/vendors/entries/${eid}/approve`);
      toast.success(`Approved. New balance: ${fmt(r.data.new_balance)}`);
      await load();
      await loadPending();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Approve failed'); }
  };

  const rejectEntry = async (eid) => {
    const reason = window.prompt('Reject karne ka reason likho:');
    if (!reason || reason.length < 3) return;
    try {
      await api.post(`/vendors/entries/${eid}/reject`, { reason });
      toast.success('Rejected');
      await load();
      await loadPending();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Reject failed'); }
  };

  const doFundAdd = async () => {
    const amt = Number(fundForm.amount);
    if (!amt || amt <= 0) { toast.error('Valid amount dalo'); return; }
    if (!fundForm.payment_date) { toast.error('Payment date select karo'); return; }
    try {
      const fd = new FormData();
      fd.append('amount', String(amt));
      fd.append('payment_mode', fundForm.payment_mode);
      Object.entries(fundForm).forEach(([k, v]) => {
        if (['amount', 'payment_mode'].includes(k)) return;
        if (v) fd.append(k, String(v));
      });
      if (fundFile) fd.append('receipt', fundFile);
      const r = await api.post(`/vendors/${vendorId}/payment`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${r.data.message}. New balance: ${fmt(r.data.new_balance)}`);
      setShowFund(false);
      setFundForm({ amount: '', payment_mode: 'upi', receipt_number: '', upi_txn_id: '', account_holder: '', account_number: '', ifsc: '', txn_reference: '', remarks: '', payment_date: new Date().toISOString().slice(0, 10) });
      setFundFile(null);
      await load();
      await loadAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fund add failed');
    }
  };

  const doDiscountUpdate = async () => {
    try {
      await api.put(`/vendors/${vendorId}/discount`, { discount_percent: Number(newDiscount) });
      toast.success('Discount updated');
      setShowDiscount(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  if (loading || !summary) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  const canUpload = isSelfVendor || (!isReadOnly && !isSelfVendor);  // vendor + admin/owner
  const canFundAdd = !isReadOnly && !isSelfVendor;                     // admin/owner only
  const canEditDiscount = !isReadOnly && !isSelfVendor;                // admin/owner only
  // For card / recharge / electricity vendors — WE receive payment FROM the vendor
  // (they owe us for cards/service). For gift_card vendor, WE pay the vendor.
  const isReceivedPaymentVendor = ['card', 'recharge', 'electricity'].includes(summary.vendor_type);

  return (
    <div className="space-y-5" data-testid={`gc-vendor-detail-${vendorId}`}>
      {/* Vendor-only complaints alert banner */}
      {isSelfVendor && <VendorComplaintsAlert onAction={load} />}

      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        {!isSelfVendor && (
          <Button variant="outline" onClick={onBack}>← Back</Button>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gift size={26} weight="duotone" className="text-fuchsia-600" />
            {summary.name}
          </h1>
          <div className="text-xs text-gray-500">{summary.email} • Discount {summary.discount_percent}%</div>
        </div>
        <div className="flex gap-2">
          {canEditDiscount && (
            <Dialog open={showDiscount} onOpenChange={setShowDiscount}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-1"><Percent size={14} /> Discount</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Update Discount %</DialogTitle></DialogHeader>
                <div>
                  <Label>New Discount %</Label>
                  <Input type="number" step="0.01" value={newDiscount} onChange={(e) => setNewDiscount(e.target.value)} data-testid="gc-new-discount" />
                  <div className="text-xs text-gray-500 mt-1">Sirf nayi uploads pe lagu hoga. Purani uploads me purana % save hai.</div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDiscount(false)}>Cancel</Button>
                  <Button onClick={doDiscountUpdate} className="bg-fuchsia-600">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {canFundAdd && (
            <Dialog open={showFund} onOpenChange={setShowFund}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="gc-fund-add-btn">
                  <CurrencyInr size={14} weight="bold" /> {isReceivedPaymentVendor ? 'Received Payment' : 'Fund Payment'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{isReceivedPaymentVendor ? 'Record Payment Received from Vendor' : 'Record Payment to Vendor'}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  {isReceivedPaymentVendor && (
                    <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-800">
                      Ye entry vendor ki taraf se aayi payment hai. Jitna amount enter karoge, utna vendor ka pending owed amount kum ho jayega (balance Cr side me add hoga).
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-1 block">Payment Date *</Label>
                      <DatePickerButton
                        value={fundForm.payment_date}
                        onChange={(d) => setFundForm({ ...fundForm, payment_date: d })}
                        testid="gc-fund-date"
                      />
                    </div>
                    <div>
                      <Label>Amount *</Label>
                      <Input type="number" step="0.01" value={fundForm.amount} onChange={(e) => setFundForm({ ...fundForm, amount: e.target.value })} data-testid="gc-fund-amount" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Payment Mode *</Label>
                      <select className="w-full h-10 px-3 border rounded-md" value={fundForm.payment_mode} onChange={(e) => setFundForm({ ...fundForm, payment_mode: e.target.value })} data-testid="gc-fund-mode">
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="neft">NEFT/IMPS/RTGS</option>
                      </select>
                    </div>
                    <div />
                  </div>

                  {fundForm.payment_mode === 'cash' && (
                    <div>
                      <Label>Receipt Number (optional)</Label>
                      <Input value={fundForm.receipt_number} onChange={(e) => setFundForm({ ...fundForm, receipt_number: e.target.value })} />
                    </div>
                  )}

                  {fundForm.payment_mode === 'upi' && (
                    <div>
                      <Label>UPI Transaction ID *</Label>
                      <Input value={fundForm.upi_txn_id} onChange={(e) => setFundForm({ ...fundForm, upi_txn_id: e.target.value })} placeholder="e.g. 401234567890" />
                    </div>
                  )}

                  {fundForm.payment_mode === 'neft' && (
                    <>
                      {savedAccounts.length > 0 && (
                        <div>
                          <Label>Saved Accounts (quick fill)</Label>
                          <select className="w-full h-10 px-3 border rounded-md text-sm" onChange={(e) => {
                            const a = savedAccounts.find(x => x.id === e.target.value);
                            if (a) setFundForm({ ...fundForm, account_holder: a.account_holder, account_number: a.account_number, ifsc: a.ifsc });
                          }}>
                            <option value="">— Select saved account —</option>
                            {savedAccounts.map(a => (
                              <option key={a.id} value={a.id}>{a.account_holder} • {a.account_number} • {a.ifsc}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Account Holder *</Label><Input value={fundForm.account_holder} onChange={(e) => setFundForm({ ...fundForm, account_holder: e.target.value })} /></div>
                        <div><Label>Account Number *</Label><Input value={fundForm.account_number} onChange={(e) => setFundForm({ ...fundForm, account_number: e.target.value })} /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>IFSC *</Label><Input value={fundForm.ifsc} onChange={(e) => setFundForm({ ...fundForm, ifsc: e.target.value.toUpperCase() })} /></div>
                        <div><Label>Txn Reference</Label><Input value={fundForm.txn_reference} onChange={(e) => setFundForm({ ...fundForm, txn_reference: e.target.value })} /></div>
                      </div>
                    </>
                  )}

                  <div>
                    <Label>Receipt / Proof (optional)</Label>
                    <Input type="file" accept="image/*,.pdf" onChange={(e) => setFundFile(e.target.files[0] || null)} />
                    {fundFile && <div className="text-[11px] text-gray-500 mt-1">{fundFile.name}</div>}
                  </div>
                  <div>
                    <Label>Remarks</Label>
                    <Input value={fundForm.remarks} onChange={(e) => setFundForm({ ...fundForm, remarks: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowFund(false)}>Cancel</Button>
                  <Button onClick={doFundAdd} className="bg-emerald-600" data-testid="gc-fund-submit">
                    {isReceivedPaymentVendor ? 'Record Received Payment' : 'Record Payment'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {summary.vendor_type === 'card' ? (
          <>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="card-vendor-total-cards">
              <div className="text-[11px] text-indigo-700 uppercase">Total Cards</div>
              <div className="text-2xl font-bold text-indigo-900">{cardSummary?.total ?? '—'}</div>
              <div className="text-[10px] text-indigo-600">Admin ne diye</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="card-vendor-unused-cards">
              <div className="text-[11px] text-emerald-700 uppercase">Unused</div>
              <div className="text-2xl font-bold text-emerald-900">{cardSummary?.unused ?? '—'}</div>
              <div className="text-[10px] text-emerald-600">Stock me baaki</div>
            </div>
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-3" data-testid="card-vendor-used-cards">
              <div className="text-[11px] text-gray-700 uppercase">Used</div>
              <div className="text-2xl font-bold text-gray-900">{cardSummary?.used ?? '—'}</div>
              <div className="text-[10px] text-gray-600">Sell/use ho gaye</div>
            </div>
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3">
              <div className="text-[11px] text-fuchsia-700 uppercase">Cards Limit Total</div>
              <div className="text-lg font-bold text-fuchsia-900">{fmt(cardSummary?.total_face || 0)}</div>
            </div>
            <div className={`border rounded-lg p-3 ${summary.balance < 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className={`text-[11px] uppercase flex items-center gap-1 ${summary.balance < 0 ? 'text-red-700' : 'text-amber-700'}`}><Wallet size={12} /> Wallet Balance</div>
              <div className={`text-xl font-bold ${summary.balance < 0 ? 'text-red-800' : 'text-amber-800'}`}>{fmt(summary.balance)}</div>
              <div className="text-[10px] opacity-70">Cr {fmt(summary.credit || 0)} / Dr {fmt(summary.debit || 0)}</div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white border rounded-lg p-3">
              <div className="text-[11px] text-gray-500 uppercase">Total Face Value</div>
              <div className="text-lg font-bold">{fmt(summary.total_face_value)}</div>
              <div className="text-[10px] text-gray-400">{summary.total_cards_uploaded} cards</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="text-[11px] text-emerald-700 uppercase">Credited (Earned)</div>
              <div className="text-lg font-bold text-emerald-800">{fmt(summary.credit)}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-[11px] text-red-700 uppercase">Paid Out</div>
              <div className="text-lg font-bold text-red-800">{fmt(summary.debit)}</div>
            </div>
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 col-span-2">
              <div className="text-[11px] text-fuchsia-700 uppercase flex items-center gap-1"><Wallet size={12} /> Balance (owed to vendor)</div>
              <div className="text-2xl font-bold text-fuchsia-800">{fmt(summary.balance)}</div>
            </div>
          </>
        )}
      </div>

      {/* ═══ Payment Receive Accounts — only for gift_card + recharge vendors ═══ */}
      {!['card', 'electricity'].includes(summary.vendor_type) && (
        <VendorBankAccounts vendorId={vendorId} canEdit={isSelfVendor || (!isReadOnly && !isSelfVendor)} />
      )}

      {/* ═══ My Gift Cards (Vendor self-view) — only for gift-card category;
            recharge/electricity vendors don't have gift-card vouchers ═══ */}
      {isSelfVendor && summary.vendor_type === 'gift_card' && <MyGiftCardsSection vendorId={vendorId} />}

      {/* ═══ Electricity Vendor — Bulk Uploads + Manual Entries + Refund gate ═══ */}
      {summary.vendor_type === 'electricity' && !isSelfVendor && !isReadOnly && (
        <ElectricityBulkSection
          vendorId={vendorId}
          vendorName={summary.name || ''}
          onLedgerChanged={async () => { await load(); }}
        />
      )}

      {/* ═══ Card Vendor — Inline Card Management (Admin/Owner/Vendor-self) ═══ */}
      {summary.vendor_type === 'card' && (
        <CardVendorInlineManager
          vendorId={vendorId}
          vendorName={summary.name || ''}
          vendorDiscount={summary.discount_percent || 0}
          canUpload={!isSelfVendor && !isReadOnly}
          canManualAdjust={!isSelfVendor && ['admin', 'owner', 'assistant', 'accountant'].includes(viewerRole)}
          onReload={async () => { await load(); }}
        />
      )}

      {/* ═══ Structured Entry Submission (Primary — goes via approval) —
            ONLY for gift_card category. Card vendors have their own inline
            manager above; recharge/electricity use Files Upload + Ledger only. ═══ */}
      {canUpload && summary.vendor_type === 'gift_card' && (
        <div className="bg-white border border-indigo-200 rounded-xl p-4 space-y-3" data-testid="gc-entry-section">
          <div className="font-semibold text-gray-900 flex items-center gap-2">
            <Receipt size={18} weight="duotone" className="text-indigo-600" />
            Submit Gift Card Entry {isSelfVendor ? '(Admin approval pending)' : ''}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Entry Date *</Label>
              <DatePickerButton
                value={entryForm.entry_date}
                onChange={(d) => setEntryForm({ ...entryForm, entry_date: d })}
                testid="entry-date"
              />
            </div>
            <div>
              <Label className="text-xs">Cards Count *</Label>
              <Input type="number" value={entryForm.card_count} onChange={(e) => setEntryForm({ ...entryForm, card_count: e.target.value })} data-testid="entry-card-count" />
            </div>
            <div>
              <Label className="text-xs">Per Card ₹ *</Label>
              <Input type="number" step="0.01" value={entryForm.per_card_amount} onChange={(e) => setEntryForm({ ...entryForm, per_card_amount: e.target.value })} data-testid="entry-per-card" />
            </div>
            <div>
              <Label className="text-xs">Discount % Override</Label>
              <Input type="number" step="0.01" placeholder={`Default ${summary.discount_percent}%`}
                value={entryForm.discount_override} onChange={(e) => setEntryForm({ ...entryForm, discount_override: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Receipt (optional)</Label>
              <Input type="file" accept="image/*,.pdf" onChange={(e) => setEntryFile(e.target.files[0] || null)} />
            </div>
            <div className="flex items-end">
              <Button onClick={submitEntry} className="w-full bg-indigo-600 hover:bg-indigo-700 gap-1" data-testid="submit-entry-btn">
                <CheckCircle size={14} weight="bold" /> Submit Entry
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Remarks</Label>
            <Input value={entryForm.remarks} onChange={(e) => setEntryForm({ ...entryForm, remarks: e.target.value })} />
          </div>
          <div className="text-[11px] text-indigo-700 bg-indigo-50 px-2 py-1.5 rounded">
            Preview: {entryForm.card_count && entryForm.per_card_amount ? (
              <>Face {fmt(Number(entryForm.card_count) * Number(entryForm.per_card_amount))} → Payable {fmt(Number(entryForm.card_count) * Number(entryForm.per_card_amount) * (1 - (Number(entryForm.discount_override || summary.discount_percent) / 100)))}</>
            ) : 'Count and per-card amount enter karo...'}
          </div>
        </div>
      )}

      {/* Export buttons + Rich Ledger Section with date-range filter — same for Admin/CA and Vendor-self */}
      <VendorLedgerSection
        vendorId={vendorId}
        vendorName={summary.name}
        entries={ledger.entries || []}
        balance={ledger.balance || { credit: summary.credit, debit: summary.debit, balance: summary.balance }}
        showBalance={true}
        onRefresh={load}
      />

      {/* Pending Entries (admin only) */}
      {!isSelfVendor && !isReadOnly && pendingEntries.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b bg-amber-50 font-semibold text-amber-900 flex items-center gap-2">
            <Clock size={18} weight="duotone" /> Pending Approvals ({pendingEntries.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-right">Cards</th>
                  <th className="px-3 py-2 text-right">Per Card</th>
                  <th className="px-3 py-2 text-right">Face</th>
                  <th className="px-3 py-2 text-right">Disc%</th>
                  <th className="px-3 py-2 text-right">Payable</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingEntries.map((e) => (
                  <tr key={e.id} className="border-t" data-testid={`pending-entry-${e.id}`}>
                    <td className="px-3 py-2 font-medium">{e.vendor_name}</td>
                    <td className="px-3 py-2 text-right">{e.card_count}</td>
                    <td className="px-3 py-2 text-right">{fmt(e.per_card_amount)}</td>
                    <td className="px-3 py-2 text-right">{fmt(e.face_value)}</td>
                    <td className="px-3 py-2 text-right">{e.discount_percent}%</td>
                    <td className="px-3 py-2 text-right font-semibold text-indigo-700">{fmt(e.payable_amount)}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-[200px] truncate">{e.remarks || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2" onClick={() => approveEntry(e.id)} data-testid={`approve-${e.id}`}>
                          <CheckCircle size={12} /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 h-7 px-2" onClick={() => rejectEntry(e.id)} data-testid={`reject-${e.id}`}>
                          <XCircle size={12} /> Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* My Entries history (vendor view) */}
      {isSelfVendor && myEntries.length > 0 && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b font-semibold text-gray-900">My Entries</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Cards × ₹</th>
                  <th className="px-3 py-2 text-right">Face</th>
                  <th className="px-3 py-2 text-right">Payable</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Note</th>
                </tr>
              </thead>
              <tbody>
                {myEntries.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="px-3 py-2 text-xs">{fmtDate(e.submitted_at)}</td>
                    <td className="px-3 py-2 text-right text-xs">{e.card_count} × {fmt(e.per_card_amount)}</td>
                    <td className="px-3 py-2 text-right">{fmt(e.face_value)}</td>
                    <td className="px-3 py-2 text-right text-indigo-700 font-semibold">{fmt(e.payable_amount)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                        e.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        e.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{e.status.toUpperCase()}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {e.status === 'rejected' ? (e.rejection_reason || '—') : (e.remarks || '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Voucher Upload — Gift Card vendors only.
            Card vendors have their own inline manager; recharge/electricity
            use Files Upload + Ledger only (no voucher paste UI). ═══ */}
      {!isReadOnly && summary.vendor_type === 'gift_card' && (
        <VoucherUploadCard
          vendorId={vendorId}
          discountPercent={summary.discount_percent}
          isSelfVendor={isSelfVendor}
          onUploaded={async () => { await load(); }}
        />
      )}
    </div>
  );
}

/** Inline Card Vendor manager — upload + recent cards table, no page navigation.
 * Shows for Admin/Owner (with upload button) and Vendor-self (read-only).
 */
function CardVendorInlineManager({ vendorId, vendorName, vendorDiscount, canUpload, canManualAdjust, onReload }) {
  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState({ total: 0, used: 0, unused: 0, total_face: 0 });
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  // ─── Manual Adjustment (WhatsApp / no card details) ────────────
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const DEFAULT_NOTE = 'card already sent on whatsapp adjusted';
  const [adj, setAdj] = useState({ kind: 'debit', amount: '', date: todayISO(), note: DEFAULT_NOTE });
  const [adjBusy, setAdjBusy] = useState(false);

  const load = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('vendor_id', vendorId);
      params.set('limit', '10');
      const [c, s] = await Promise.all([
        api.get(`/vendor-cards/?${params.toString()}`),
        api.get(`/vendor-cards/summary?vendor_id=${vendorId}`),
      ]);
      setCards(c.data?.cards || c.data || []);
      setSummary(s.data || { total: 0, used: 0, unused: 0, total_face: 0 });
    } catch (err) {
      console.error('Card vendor load failed:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (card) => {
    const confirmed = window.confirm(
      `Card **** ${card.card_number_last4 || card.card_number?.slice(-4) || '?'} delete karna hai?\n\n` +
      `Limit: ₹${(card.card_limit || 0).toFixed(2)}\n` +
      `Status: ${(card.status || 'unused').toUpperCase()}\n\n` +
      `Ledger me proportional credit reversal ho jayega (${vendorDiscount}% discount considering). Sure?`
    );
    if (!confirmed) return;
    const cardId = card.id || card._id;
    setDeletingId(cardId);
    try {
      const r = await api.delete(`/vendor-cards/${cardId}`);
      toast.success(`Card deleted · Wallet credited back: ₹${(r.data?.reversed_amount || 0).toFixed(2)}`);
      await load();
      if (onReload) await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally { setDeletingId(''); }
  };

  const submitAdjustment = async () => {
    const amt = parseFloat(adj.amount);
    if (!amt || amt <= 0) return toast.error('Amount daalo');
    setAdjBusy(true);
    try {
      const r = await api.post('/vendor-cards/manual-adjust', {
        vendor_id: vendorId,
        kind: adj.kind,
        amount: amt,
        entry_date: adj.date || null,
        note: (adj.note || '').trim() || DEFAULT_NOTE,
      });
      const badge = r.data?.back_dated ? ' (back-dated)' : '';
      toast.success(`${adj.kind === 'debit' ? 'Debit' : 'Credit'} ₹${amt} entry posted${badge}`);
      setAdj({ kind: 'debit', amount: '', date: todayISO(), note: DEFAULT_NOTE });
      await load();
      if (onReload) await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setAdjBusy(false); }
  };

  function mask(n) {
    if (!n) return '';
    const s = String(n);
    return s.length <= 4 ? s : `${s.slice(0, 4)} •••• •••• ${s.slice(-4)}`;
  }

  return (
    <div className="bg-white border-2 border-indigo-200 rounded-xl overflow-hidden" data-testid="card-vendor-inline-manager">
      {/* Header */}
      <div className="bg-indigo-50 border-b border-indigo-200 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CreditCard size={20} weight="duotone" className="text-indigo-700" />
          <span className="font-semibold text-indigo-900">Card Inventory</span>
          <span className="text-xs text-indigo-600">({summary.total} cards · {summary.unused} unused)</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setShowSecrets((v) => !v)} className="gap-1 text-xs h-8">
            {showSecrets ? <EyeSlash size={12} /> : <Eye size={12} />}
            {showSecrets ? 'Hide' : 'Show'} Details
          </Button>
          <Button size="sm" variant="outline" onClick={load} className="gap-1 text-xs h-8">
            <ArrowClockwise size={12} /> Refresh
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { window.location.href = `/admin/vendor-cards?vendor_id=${vendorId}`; }}
            className="gap-1 text-xs h-8"
            data-testid="view-full-cards-page-btn"
          >
            <ArrowSquareOut size={12} /> Full Page
          </Button>
          {canUpload && (
            <Button
              size="sm"
              onClick={() => setShowUpload(true)}
              className="bg-indigo-600 hover:bg-indigo-700 gap-1 text-xs h-8"
              data-testid="inline-add-cards-btn"
            >
              <UploadSimple size={12} /> Add Cards
            </Button>
          )}
        </div>
      </div>

      {/* ═══ Manual Adjustment strip — for WhatsApp-sent cards (no details) ═══ */}
      {canManualAdjust && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-testid="card-manual-adjust-strip">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">Add Manual Entry</span>
            <span className="text-[10px] text-amber-700">— WhatsApp pe cards diye hain lekin details nahi? Sirf amount adjust karke settle karo</span>
          </div>
          <div className="flex flex-wrap gap-2 items-start">
            <select
              value={adj.kind}
              onChange={(e) => setAdj({ ...adj, kind: e.target.value })}
              className="h-9 px-2 text-sm border border-gray-300 rounded bg-white"
              data-testid="adj-kind"
            >
              <option value="debit">Adjustment (−) · Cards sent</option>
              <option value="credit">Adjustment (+) · Refund/Return</option>
            </select>
            <Input
              type="number" step="0.01" min="0"
              value={adj.amount}
              onChange={(e) => setAdj({ ...adj, amount: e.target.value })}
              placeholder="Amount"
              className="h-9 w-32 text-sm"
              data-testid="adj-amount"
            />
            <Input
              type="date"
              value={adj.date}
              max={todayISO()}
              onChange={(e) => setAdj({ ...adj, date: e.target.value || todayISO() })}
              className="h-9 w-40 text-sm"
              data-testid="adj-date"
            />
            <Input
              value={adj.note}
              onChange={(e) => setAdj({ ...adj, note: e.target.value })}
              placeholder="Note (default already filled)"
              className="h-9 min-w-64 flex-1 text-sm"
              data-testid="adj-note"
            />
            <Button
              onClick={submitAdjustment}
              disabled={adjBusy || !adj.amount}
              className={`h-9 gap-1 text-xs ${adj.kind === 'debit' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}
              data-testid="adj-post-btn"
            >
              <Plus size={13} weight="bold" /> {adjBusy ? 'Posting…' : 'Post'}
            </Button>
            {adj.note !== DEFAULT_NOTE && (
              <button
                type="button"
                onClick={() => setAdj({ ...adj, note: DEFAULT_NOTE })}
                className="text-[10px] font-semibold px-2 h-9 border border-amber-300 rounded hover:bg-amber-100"
                title="Reset note to default"
              >Reset Note</button>
            )}
          </div>
        </div>
      )}

      {/* Recent Cards Table */}
      {loading ? (
        <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
      ) : cards.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">
          <CreditCard size={32} weight="duotone" className="mx-auto text-gray-300 mb-2" />
          Abhi tak koi card upload nahi hua.
          {canUpload && <> Upar "Add Cards" click karke shuru karo.</>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Card Number</th>
                <th className="px-3 py-2 text-left">Expiry / CVV</th>
                <th className="px-3 py-2 text-right">Limit</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-left">Added</th>
                {canUpload && <th className="px-3 py-2 text-center w-16">Action</th>}
              </tr>
            </thead>
            <tbody>
              {cards.slice(0, 10).map((c) => (
                <tr key={c.id || c._id} className="border-t hover:bg-indigo-50/30" data-testid={`inline-card-row-${c.id || c._id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-gray-900">
                    {showSecrets ? c.card_number : mask(c.card_number)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {showSecrets ? (
                      <>
                        {c.expiry || '—'} · CVV: {c.cvv || '—'}
                      </>
                    ) : (
                      <span className="text-gray-400 italic">hidden</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-indigo-900">{fmt(c.card_limit || 0)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                      c.status === 'used' ? 'bg-gray-100 text-gray-700'
                      : c.status === 'unused' ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                    }`}>
                      {(c.status || 'unused').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(c.created_at)}</td>
                  {canUpload && (
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => handleDelete(c)}
                        disabled={deletingId === (c.id || c._id) || c.status === 'used'}
                        className="p-1.5 rounded text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={c.status === 'used' ? 'Used card cannot be deleted' : `Delete this card & reverse debit`}
                        data-testid={`inline-card-delete-${c.id || c._id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {summary.total > 10 && (
            <div className="px-4 py-2 text-center text-xs text-gray-500 border-t bg-gray-50">
              Showing 10 of {summary.total} cards · <a href={`/admin/vendor-cards?vendor_id=${vendorId}`} className="text-indigo-600 hover:underline font-medium">View all →</a>
            </div>
          )}
        </div>
      )}

      {/* Upload Dialog */}
      {canUpload && (
        <AdminCardUploadDialog
          open={showUpload}
          onOpenChange={setShowUpload}
          vendorId={vendorId}
          vendorName={vendorName}
          defaultDiscount={vendorDiscount}
          onDone={async () => {
            setShowUpload(false);
            await load();
            if (onReload) await onReload();
          }}
        />
      )}
    </div>
  );
}


function VendorComplaintsAlert({ onAction }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDlg, setShowDlg] = useState(false);
  const [active, setActive] = useState(null);
  const [counter, setCounter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/voucher-stock/vendor-complaints');
      setItems(r.data || []);
    } catch (err) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const openCounter = (c) => { setActive(c); setCounter(''); setShowDlg(true); };
  const submit = async () => {
    if (counter.trim().length < 3) { toast.error('Counter-query likho'); return; }
    try {
      await api.post(`/voucher-stock/complaints/${active.id}/counter-query`, { counter_query: counter });
      toast.success('Counter-query raised — Admin review karenge');
      setShowDlg(false);
      await load();
      if (onAction) await onAction();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  if (loading || items.length === 0) return null;
  const openComp = items.filter((c) => c.status === 'open');
  const underReview = items.filter((c) => c.status === 'under_review');
  const resolved = items.filter((c) => c.status === 'resolved');

  return (
    <>
      <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 shadow-sm" data-testid="vendor-complaints-alert">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Gift size={20} className="text-red-600" />
          <h3 className="font-bold text-red-900">🚨 Complaints on Your Vouchers ({items.length})</h3>
          <span className="text-[11px] text-red-700 ml-auto">Auto-refreshes every 30s</span>
        </div>
        <div className="flex gap-2 text-xs mb-3 flex-wrap">
          {openComp.length > 0 && <span className="bg-red-100 text-red-700 px-2 py-1 rounded font-semibold">🔴 {openComp.length} OPEN — Action needed</span>}
          {underReview.length > 0 && <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded font-semibold">🟠 {underReview.length} Under Admin Review</span>}
          {resolved.length > 0 && <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">✓ {resolved.length} Resolved</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-red-700">
              <tr>
                <th className="px-2 py-1 text-left">Ticket</th>
                <th className="px-2 py-1 text-left">Voucher</th>
                <th className="px-2 py-1 text-left">Type</th>
                <th className="px-2 py-1 text-right">Debit</th>
                <th className="px-2 py-1 text-left">Status</th>
                <th className="px-2 py-1 text-left">Raised By</th>
                <th className="px-2 py-1 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 10).map((c) => (
                <tr key={c.id} className="border-t border-red-200 hover:bg-red-100/40">
                  <td className="px-2 py-1.5 font-mono text-xs font-bold">{c.ticket_no}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{c.voucher_code}</td>
                  <td className="px-2 py-1.5 text-xs">{c.complaint_type} • <span className="text-gray-500">{c.details?.substring(0, 30)}{c.details?.length > 30 ? '…' : ''}</span></td>
                  <td className="px-2 py-1.5 text-right font-semibold text-red-700">{fmt(c.credited_amount)}</td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      c.status === 'open' ? 'bg-red-100 text-red-700' :
                      c.status === 'under_review' ? 'bg-orange-100 text-orange-700' :
                      c.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100'
                    }`}>{c.status?.replace('_', ' ').toUpperCase()}</span>
                    {c.in_favor_of && <div className="text-[9px] text-gray-600 mt-0.5">Favor: {c.in_favor_of}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-xs">{c.raised_by_name} <span className="text-gray-400">({c.raised_by_type})</span></td>
                  <td className="px-2 py-1.5 text-right">
                    {c.status === 'open' && (
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700 h-6 text-xs" onClick={() => openCounter(c)} data-testid={`counter-query-${c.id}`}>
                        Dispute
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDlg} onOpenChange={setShowDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Raise Counter-Query — {active?.ticket_no}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-xs bg-gray-50 p-2 rounded">
                <div><span className="text-gray-500">Voucher:</span> {active.voucher_code}</div>
                <div><span className="text-gray-500">Claimed issue:</span> {active.complaint_type} — {active.details}</div>
                <div><span className="text-gray-500">Raised by:</span> {active.raised_by_name}</div>
              </div>
              <Label className="text-xs">Aapka jawab (counter-query) *</Label>
              <textarea rows={4} className="w-full px-3 py-2 border rounded text-sm"
                value={counter} onChange={(e) => setCounter(e.target.value)}
                placeholder="Mere paas ye voucher fresh tha, staff se recheck karwao please..." />
              <div className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded">
                Counter-query submit karne pe ticket "Under Admin Review" status me chala jayega. Admin decide karega kiska paksh sahi hai.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDlg(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-counter-query-btn">
              Submit Counter-Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** ═══ Vendor Bank Accounts — add/list/delete (Vendor + Admin, CA read-only) ═══ */
function VendorBankAccounts({ vendorId, canEdit }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [form, setForm] = useState({ account_holder: '', account_number: '', ifsc: '', bank_name: '', account_type: 'savings', upi_id: '', is_primary: false });
  const [bulkText, setBulkText] = useState('');

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/vendors/${vendorId}/saved-accounts`); setAccounts(r.data || []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [vendorId]);  // eslint-disable-line

  const add = async () => {
    if (!form.account_holder && !form.upi_id) { toast.error('Account holder ya UPI ID zaroor daalein'); return; }
    try {
      await api.post(`/vendors/${vendorId}/saved-accounts`, form);
      toast.success('Account added');
      setShowAdd(false);
      setForm({ account_holder: '', account_number: '', ifsc: '', bank_name: '', account_type: 'savings', upi_id: '', is_primary: false });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Add failed'); }
  };

  const doBulk = async () => {
    // Parse: each line = "holder | account_number | ifsc | bank_name"  OR JSON array
    const lines = bulkText.split('\n').map((l) => l.trim()).filter(Boolean);
    const accs = [];
    for (const line of lines) {
      const parts = line.split(/\s*\|\s*|\s*,\s*|\t/).map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 1) {
        accs.push({
          account_holder: parts[0] || '',
          account_number: parts[1] || '',
          ifsc: (parts[2] || '').toUpperCase(),
          bank_name: parts[3] || '',
          upi_id: parts[4] || '',
          account_type: 'savings',
          is_primary: false,
        });
      }
    }
    if (!accs.length) { toast.error('Koi account parse nahi hua'); return; }
    try {
      const r = await api.post(`/vendors/${vendorId}/saved-accounts/bulk`, { accounts: accs });
      toast.success(r.data.message);
      setShowBulk(false);
      setBulkText('');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try { await api.delete(`/vendors/${vendorId}/saved-accounts/${id}`); toast.success('Removed'); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="bg-white border border-blue-200 rounded-xl p-4" data-testid="vendor-bank-accounts">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="font-semibold text-gray-900 flex items-center gap-2">
          <Buildings size={18} className="text-blue-600" /> Payment Receive Accounts ({accounts.length})
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowBulk(true)} data-testid="acc-bulk-add">+ Bulk Upload</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowAdd(true)} data-testid="acc-single-add">+ Add Account</Button>
          </div>
        )}
      </div>
      {loading ? <div className="text-xs text-gray-500">Loading...</div>
      : accounts.length === 0 ? <div className="text-xs text-gray-500 text-center py-3">Koi account abhi tak add nahi hua. Upar se add karein.</div>
      : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Holder</th>
                <th className="px-2 py-1.5 text-left">A/C Number</th>
                <th className="px-2 py-1.5 text-left">IFSC</th>
                <th className="px-2 py-1.5 text-left">Bank</th>
                <th className="px-2 py-1.5 text-left">UPI</th>
                <th className="px-2 py-1.5 text-center">Primary</th>
                {canEdit && <th className="px-2 py-1.5 text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} className="border-t" data-testid={`acc-row-${a.id}`}>
                  <td className="px-2 py-1.5 font-semibold">{a.account_holder}</td>
                  <td className="px-2 py-1.5 font-mono">{a.account_number || '—'}</td>
                  <td className="px-2 py-1.5 font-mono">{a.ifsc || '—'}</td>
                  <td className="px-2 py-1.5">{a.bank_name || '—'}</td>
                  <td className="px-2 py-1.5 font-mono text-indigo-700">{a.upi_id || '—'}</td>
                  <td className="px-2 py-1.5 text-center">{a.is_primary ? <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">PRIMARY</span> : ''}</td>
                  {canEdit && <td className="px-2 py-1.5 text-right"><button className="text-red-600 hover:underline text-[10px]" onClick={() => del(a.id)} data-testid={`acc-del-${a.id}`}>Remove</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Payment Receive Account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Account Holder *</Label><Input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} /></div>
              <div><Label className="text-xs">Bank Name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="HDFC / SBI / ..." /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Account Number</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></div>
              <div><Label className="text-xs">IFSC</Label><Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Type</Label>
                <select className="w-full h-10 px-3 border rounded-md text-sm" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
                  <option value="savings">Savings</option>
                  <option value="current">Current</option>
                </select>
              </div>
              <div><Label className="text-xs">UPI ID (optional)</Label><Input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="xxx@paytm / xxx@upi" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} /> Set as primary</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={add} className="bg-blue-600 hover:bg-blue-700" data-testid="acc-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk Add Accounts</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-gray-600">Ek line me ek account, pipe/comma/tab separator: <code className="bg-gray-100 px-1 rounded">Holder | A/C Number | IFSC | Bank | UPI</code></div>
            <textarea rows={8} className="w-full px-3 py-2 border rounded text-xs font-mono"
              placeholder="Ram Kumar | 1234567890 | HDFC0001234 | HDFC Bank | ram@paytm&#10;Shyam Ji | 0987654321 | SBIN0005678 | SBI | shyam@upi"
              value={bulkText} onChange={(e) => setBulkText(e.target.value)}
              data-testid="acc-bulk-text" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button>
            <Button onClick={doBulk} className="bg-blue-600 hover:bg-blue-700" data-testid="acc-bulk-submit">Upload Accounts</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** ═══ My Gift Cards Section (Vendor self-view + Excel export) ═══ */
function MyGiftCardsSection({ vendorId }) {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/vendors/${vendorId}/my-vouchers`); setVouchers(r.data || []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [vendorId]);  // eslint-disable-line

  const exportExcel = async () => {
    try {
      const r = await api.get(`/vendors/${vendorId}/my-vouchers/excel`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `my_vouchers_${Date.now()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('Export failed'); }
  };

  const filtered = statusFilter === 'all' ? vouchers : vouchers.filter((v) => v.status === statusFilter);
  const totals = filtered.reduce((acc, v) => ({
    face: acc.face + (v.face_value || 0),
    credit: acc.credit + (v.credited_amount || 0),
  }), { face: 0, credit: 0 });

  const byStatus = vouchers.reduce((acc, v) => { acc[v.status || 'available'] = (acc[v.status || 'available'] || 0) + 1; return acc; }, {});

  return (
    <div className="bg-white border border-fuchsia-200 rounded-xl p-4" data-testid="my-gift-cards-section">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="font-semibold text-gray-900 flex items-center gap-2">
          <Gift size={18} className="text-fuchsia-600" /> My Gift Cards ({vouchers.length})
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {['all', 'available', 'assigned', 'sold', 'complaint', 'reversed'].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-2 py-1 text-[10px] font-semibold rounded border ${statusFilter === s ? 'bg-fuchsia-600 border-fuchsia-600 text-white' : 'bg-white border-gray-300'}`}
              data-testid={`my-gc-filter-${s}`}>
              {s.toUpperCase()} {s !== 'all' && byStatus[s] ? `(${byStatus[s]})` : ''}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={exportExcel} className="gap-1" data-testid="my-gc-export-btn">📊 Excel</Button>
        </div>
      </div>
      <div className="text-xs text-gray-600 mb-2">
        Showing <b>{filtered.length}</b> vouchers • Total face <b>{fmt(totals.face)}</b> • Credited <b className="text-emerald-700">{fmt(totals.credit)}</b>
      </div>
      {loading ? <div className="text-center text-xs text-gray-500 py-4">Loading...</div>
      : filtered.length === 0 ? <div className="text-center text-xs text-gray-500 py-4">Koi voucher is filter me nahi</div>
      : (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
              <tr>
                <th className="px-2 py-1.5 text-left">#</th>
                <th className="px-2 py-1.5 text-left">Upload Date</th>
                <th className="px-2 py-1.5 text-left">Code</th>
                <th className="px-2 py-1.5 text-right">Face ₹</th>
                <th className="px-2 py-1.5 text-right">Credit ₹</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-left">Assigned Staff</th>
                <th className="px-2 py-1.5 text-left">Sold To</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v, i) => (
                <tr key={v.id} className={`border-t ${v.has_complaint ? 'bg-amber-50' : ''}`}>
                  <td className="px-2 py-1.5 text-gray-400">{i + 1}</td>
                  <td className="px-2 py-1.5 text-[11px]">{v.created_at ? new Date(v.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td className="px-2 py-1.5 font-mono">{v.code}</td>
                  <td className="px-2 py-1.5 text-right font-semibold">{fmt(v.face_value)}</td>
                  <td className="px-2 py-1.5 text-right text-emerald-700">{fmt(v.credited_amount)}</td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                      v.status === 'available' ? 'bg-emerald-100 text-emerald-700' :
                      v.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                      v.status === 'sold' ? 'bg-indigo-100 text-indigo-700' :
                      v.status === 'complaint' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>{v.status?.toUpperCase()}</span>
                  </td>
                  <td className="px-2 py-1.5">{v.assigned_to_staff_name || '—'}</td>
                  <td className="px-2 py-1.5">{v.sold_to_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** ═══ Voucher Upload Card — 3 input modes with live preview ═══ */
function VoucherUploadCard({ vendorId, discountPercent, isSelfVendor, onUploaded }) {
  const [mode, setMode] = useState('grouped'); // grouped | line | excel
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Client-side parsers (mirror backend for live preview)
  const parseGrouped = useCallback((raw) => {
    const items = [];
    let currentAmount = null;
    const headerRe = /^[₹rsRS.\s]*([0-9][0-9,]*(?:\.[0-9]+)?)\s*[:\-]?\s*$/;
    for (const r of (raw || '').split('\n')) {
      const line = r.trim();
      if (!line) continue;
      const m = line.match(headerRe);
      if (m) {
        const val = parseFloat(m[1].replace(/,/g, ''));
        if (!isNaN(val) && val > 0) { currentAmount = val; continue; }
      }
      if (!currentAmount || currentAmount <= 0) continue;
      const code = line.split(/\s+/)[0].replace(/,$/, '').trim();
      if (code) items.push({ code, amount: currentAmount });
    }
    return items;
  }, []);

  const parseLine = useCallback((raw) => {
    const items = [];
    for (const r of (raw || '').split('\n')) {
      const line = r.trim();
      if (!line) continue;
      const parts = line.split(/[,\t]+|\s{2,}|\s+/).filter(Boolean);
      if (!parts.length) continue;
      const code = parts[0];
      let amount = null;
      for (const p of parts.slice(1)) {
        const v = parseFloat(p.replace(/[,₹]/g, ''));
        if (!isNaN(v)) { amount = v; break; }
      }
      if (code && amount && amount > 0) items.push({ code, amount });
    }
    return items;
  }, []);

  const parseExcel = useCallback(async (f) => {
    if (!f) return [];
    const XLSX = await import('xlsx');
    const buf = await f.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
    const items = [];
    // Detect header
    const first = rows[0] || [];
    const firstIsHeader = first.every(c => c === null || (typeof c === 'string' && isNaN(parseFloat(c))));
    const dataRows = firstIsHeader ? rows.slice(1) : rows;
    for (const r of dataRows) {
      if (!r || r.every(c => c === null || c === '')) continue;
      let code = null, amount = null;
      for (const c of r) {
        if (c === null || c === '') continue;
        if (typeof c === 'number' && amount === null) amount = c;
        else if (typeof c === 'string') {
          const s = c.trim(); if (!s) continue;
          const v = parseFloat(s.replace(/[,₹]/g, ''));
          if (!isNaN(v)) { if (amount === null) amount = v; }
          else if (code === null) code = s;
        }
      }
      if (code && amount && amount > 0) items.push({ code, amount });
    }
    return items;
  }, []);

  // Re-parse on input change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setParsing(true);
      try {
        let items = [];
        if (mode === 'grouped') items = parseGrouped(text);
        else if (mode === 'line') items = parseLine(text);
        else if (mode === 'excel') items = await parseExcel(file);
        if (!cancelled) setPreview(items);
      } catch (err) {
        if (!cancelled) setPreview([]);
      } finally {
        if (!cancelled) setParsing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, text, file, parseGrouped, parseLine, parseExcel]);

  const totalFace = preview.reduce((s, x) => s + (x.amount || 0), 0);
  const totalCredit = totalFace * (1 - (discountPercent || 0) / 100);

  const [showConfirm, setShowConfirm] = useState(false);

  const requestUpload = () => {
    if (!preview.length) { toast.error('Koi valid voucher parse nahi hua'); return; }
    setShowConfirm(true);
  };

  const handleUpload = async () => {
    if (!preview.length) return;
    setShowConfirm(false);
    setUploading(true);
    try {
      const fd = new FormData();
      if (mode === 'excel' && file) {
        fd.append('file', file);
      } else {
        fd.append('text', text);
        fd.append('mode', mode);
      }
      const r = await api.post(`/vendors/${vendorId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(r.data.message + (r.data.skipped_duplicates ? ` (${r.data.skipped_duplicates} duplicates skipped)` : ''));
      setText(''); setFile(null); setPreview([]);
      if (onUploaded) await onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const modeBtn = (key, label) => (
    <button
      type="button"
      onClick={() => { setMode(key); setText(''); setFile(null); setPreview([]); }}
      className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition ${
        mode === key ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
      data-testid={`voucher-mode-${key}`}
    >
      {label}
    </button>
  );

  // Group preview by amount for display
  const grouped = preview.reduce((acc, x) => {
    const k = String(x.amount);
    if (!acc[k]) acc[k] = [];
    acc[k].push(x.code);
    return acc;
  }, {});

  return (
    <div className="bg-white border border-fuchsia-200 rounded-xl p-4 space-y-3" data-testid="voucher-upload-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-semibold text-gray-900 flex items-center gap-2">
          <UploadSimple size={18} weight="duotone" className="text-fuchsia-600" />
          Voucher Upload {isSelfVendor ? '(Self — your vouchers)' : '(Admin)'}
          <span className="text-[11px] font-normal text-gray-500">• Discount {discountPercent}%</span>
        </div>
        <div className="flex gap-2">
          {modeBtn('grouped', '1. Grouped (Denomination-wise)')}
          {modeBtn('line', '2. Per Line (Code + Amount)')}
          {modeBtn('excel', '3. Excel Upload')}
        </div>
      </div>

      {mode === 'grouped' && (
        <div>
          <Label className="text-xs">Paste grouped vouchers — amount header, then codes below</Label>
          <Textarea
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'5000:\nH4W3-XB3FK4-WVJR\nFFT3-JVK673-PUDT\n\n2500:\nNQ7EF73MYNK8AF\n\n2000:\n33M8P4T4TYRQFB'}
            className="font-mono text-xs"
            data-testid="voucher-grouped-text"
          />
          <div className="text-[11px] text-gray-500 mt-1">Amount ke baad colon (5000:), phir niche codes. Blank line se naya group.</div>
        </div>
      )}

      {mode === 'line' && (
        <div>
          <Label className="text-xs">Paste one voucher per line — Code [tab/space] Amount</Label>
          <Textarea
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'H4W3-XB3FK4-WVJR\t5000\nFFT3-JVK673-PUDT\t5000\nNQ7EF73MYNK8AF\t2500'}
            className="font-mono text-xs"
            data-testid="voucher-line-text"
          />
          <div className="text-[11px] text-gray-500 mt-1">Separator: tab, comma, space, ya multi-space — koi bhi chalega.</div>
        </div>
      )}

      {mode === 'excel' && (
        <div>
          <Label className="text-xs">Upload Excel (.xlsx) — Column A = Code, Column B = Amount</Label>
          <Input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files[0] || null)}
            data-testid="voucher-excel-file"
          />
          {file && <div className="text-[11px] text-gray-500 mt-1">{file.name}</div>}
        </div>
      )}

      {/* Live Preview */}
      {(preview.length > 0 || parsing) && (
        <div className="border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-fuchsia-50 border-b flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs font-semibold text-fuchsia-900">
              Preview: {preview.length} vouchers
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-gray-600">Total Face: <span className="font-bold text-gray-900">{fmt(totalFace)}</span></span>
              <span className="text-emerald-700">Will Credit: <span className="font-bold">{fmt(totalCredit)}</span></span>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left w-10">#</th>
                  <th className="px-2 py-1.5 text-left">Voucher Code</th>
                  <th className="px-2 py-1.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(grouped).sort((a, b) => Number(b) - Number(a)).map((amt) => (
                  <>
                    <tr key={`hdr-${amt}`} className="bg-indigo-50">
                      <td colSpan={3} className="px-2 py-1 font-semibold text-indigo-700">
                        ₹{Number(amt).toLocaleString('en-IN')} × {grouped[amt].length} vouchers = {fmt(Number(amt) * grouped[amt].length)}
                      </td>
                    </tr>
                    {grouped[amt].map((code, i) => (
                      <tr key={`${amt}-${i}`} className="border-t">
                        <td className="px-2 py-1 text-gray-400">{i + 1}</td>
                        <td className="px-2 py-1 font-mono">{code}</td>
                        <td className="px-2 py-1 text-right">{fmt(Number(amt))}</td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={requestUpload}
          disabled={uploading || parsing || preview.length === 0}
          className="bg-fuchsia-600 hover:bg-fuchsia-700 gap-1"
          data-testid="voucher-upload-submit"
        >
          <UploadSimple size={14} weight="bold" />
          {uploading ? 'Uploading...' : `Upload ${preview.length} Vouchers • Credit ${fmt(totalCredit)}`}
        </Button>
      </div>

      {/* Confirmation Dialog — user must explicitly Yes-confirm before upload */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>🔔 Upload Confirmation — Final Check</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded p-3">
              <div className="font-semibold text-fuchsia-900 mb-2">Upload Summary</div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-gray-700">Total Vouchers:</span><span className="font-semibold">{preview.length}</span></div>
                <div className="flex justify-between"><span className="text-gray-700">Total Face Value:</span><span className="font-semibold">{fmt(totalFace)}</span></div>
                <div className="flex justify-between"><span className="text-gray-700">Discount %:</span><span className="font-semibold">{discountPercent}%</span></div>
                <div className="flex justify-between"><span className="text-gray-700">Will Credit (to vendor ledger):</span><span className="font-semibold text-emerald-700">{fmt(totalCredit)}</span></div>
              </div>
              {Object.keys(grouped).length > 0 && (
                <div className="mt-3 pt-2 border-t border-fuchsia-200 text-[11px] text-gray-600">
                  Denomination breakdown: {Object.entries(grouped).sort((a, b) => Number(b[0]) - Number(a[0])).map(([amt, codes]) => `₹${Number(amt).toLocaleString('en-IN')} × ${codes.length}`).join(' + ')}
                </div>
              )}
            </div>
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              ⚠️ Upload confirm karne se yeh vouchers permanent record ho jayenge aur ledger me turant credit lag jayega. Agar kuch galat hai to <b>Back</b> dabaye aur theek karein.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="upload-confirm-back-btn">← Back & Edit</Button>
            <Button onClick={handleUpload} className="bg-emerald-600 hover:bg-emerald-700" data-testid="upload-confirm-yes-btn">
              ✓ Yes, Upload {preview.length} Vouchers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Top-level page */
export default function GiftCardVendorsPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'admin' || role === 'owner';
  const isAssistant = role === 'assistant';
  const isCA = role === 'accountant';
  const vendorRoles = ['gift_card_vendor', 'recharge_vendor', 'electricity_vendor', 'card_vendor'];
  const isVendor = vendorRoles.includes(role);

  // If URL has /vendors/:id — admin is viewing a specific vendor
  const pathParts = typeof window !== 'undefined' ? window.location.pathname.split('/') : [];
  const detailId = pathParts[1] === 'vendors' && pathParts[2] ? pathParts[2] : null;

  if (!isAdmin && !isAssistant && !isCA && !isVendor) {
    return <div className="p-6 text-red-600">Access denied</div>;
  }
  if (isVendor) {
    return <VendorDetail vendorId={user.id} onBack={() => {}} isReadOnly={false} isSelfVendor />;
  }
  // Admin + Assistant: full access. CA: read-only for card upload but can manual-adjust.
  const readOnly = isCA;
  if (detailId) {
    return <VendorDetail vendorId={detailId} onBack={() => window.history.back()} isReadOnly={readOnly} viewerRole={role} />;
  }
  return <AdminView isReadOnly={readOnly} viewerRole={role} />;
}
